import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Search } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { CommentaryAccessNotice, isAccessError } from "@/components/commentary/CommentaryAccess";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCommentaries } from "@/components/commentary/CommentaryPanel";

export function CommentarySearchBox({ slug, initial = "", onSubmit, autoFocus = false }) {
  const [q, setQ] = useState(initial);
  useEffect(() => { setQ(initial); }, [initial]);
  const { data: list } = useCommentaries();
  const name = list?.find((c) => c.slug === slug)?.name || "this commentary";
  return (
    <form onSubmit={(e) => { e.preventDefault(); if (q.trim()) onSubmit(q.trim()); }} className="flex gap-2" data-testid="commentary-search-form">
      <div className="relative flex-1">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} autoFocus={autoFocus} placeholder={`Search ${name}… e.g. "born again", "shepherd", "Where art thou"`} className="h-11 rounded-full pl-9" data-testid="commentary-search-input" />
      </div>
      <Button type="submit" className="h-11 rounded-full bg-primary px-5 text-primary-foreground" data-testid="commentary-search-submit">Search</Button>
    </form>
  );
}

function mark(text, q) {
  const words = q.toLowerCase().match(/[a-z0-9']+/g) || [];
  if (!words.length) return text;
  const re = new RegExp(`(${words.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`, "ig");
  return text.split(re).map((part, i) => (words.includes(part.toLowerCase()) ? <mark key={`${i}-${part}`} className="rounded-sm bg-gold/30 px-0.5 text-foreground">{part}</mark> : part));
}

export default function CommentarySearch() {
  const { slug } = useParams();
  const [sp, setSp] = useSearchParams();
  const q = sp.get("q") || "";
  const book = sp.get("book") || "";
  const { data: list } = useCommentaries();
  const name = list?.find((c) => c.slug === slug)?.name || "Commentary";
  const { data: meta, error: metaError } = useQuery({ queryKey: ["commentary-books", slug], queryFn: async () => (await api.get(`/commentaries/${slug}`)).data, staleTime: 60 * 60 * 1000, retry: (n, e) => !isAccessError(e) && n < 2 });
  const { data, isFetching } = useQuery({
    queryKey: ["commentary-search", slug, q, book],
    queryFn: async () => (await api.get(`/commentaries/${slug}/search`, { params: { q, book: book || undefined, limit: 50 } })).data,
    enabled: !!q,
    keepPreviousData: true,
  });
  const set = (k, v) => { const n = new URLSearchParams(sp); if (v) n.set(k, v); else n.delete(k); setSp(n); };
  return (
    <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-8" data-testid="commentary-search-page">
      <Seo title={q ? `“${q}” in ${name}` : `Search ${name}`} />
      {metaError && isAccessError(metaError) && <div className="mb-6"><CommentaryAccessNotice error={metaError} /></div>}
      <Link to={`/commentary/${slug}`} className="font-mono text-xs uppercase tracking-[0.25em] text-gold hover:underline">{name}</Link>
      <h1 className="mt-2 font-serif text-3xl font-bold tracking-tight text-foreground sm:text-4xl">Search the commentary</h1>
      <div className="mt-6"><CommentarySearchBox slug={slug} initial={q} autoFocus onSubmit={(v) => set("q", v)} /></div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Select value={book || "all"} onValueChange={(v) => set("book", v === "all" ? "" : v)}>
          <SelectTrigger className="h-9 w-[200px] rounded-full font-serif" data-testid="commentary-search-book"><SelectValue placeholder="All books" /></SelectTrigger>
          <SelectContent className="max-h-[60vh]"><SelectItem value="all">All books</SelectItem>{meta?.books.map((b) => <SelectItem key={b.book} value={b.book}>{b.book}</SelectItem>)}</SelectContent>
        </Select>
        {q && data && <p className="font-mono text-xs text-muted-foreground" data-testid="commentary-search-count">{data.total.toLocaleString()} section{data.total === 1 ? "" : "s"} mention “{q}”{data.total > data.results.length ? ` · showing the best ${data.results.length}` : ""}</p>}
        {isFetching && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
      </div>
      <div className="mt-6 space-y-3">
        {q && data?.results.length === 0 && <p className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="commentary-search-empty">Nothing found. Try fewer or different words.</p>}
        {data?.results.map((r) => (
          <Link key={r.id} to={r.href} className="block rounded-2xl border border-border bg-card p-5 transition-all hover:border-gold hover:shadow-md" data-testid={`commentary-result-${r.id}`}>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="no-reftag font-mono text-[11px] uppercase tracking-wider text-gold">{r.reference}</span>
              {r.title && <span className="font-serif text-base text-foreground">{r.title}</span>}
            </div>
            <p className="mt-2 font-serif text-[15px] leading-relaxed text-foreground/85">{mark(r.snippet, q)}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}
